import React from 'react';

import type { TimeChartData } from 'ui/shared/chart/types';

import { useColorModeValue } from 'toolkit/chakra/color-mode';
import ChartArea from 'ui/shared/chart/ChartArea';
import ChartAxis from 'ui/shared/chart/ChartAxis';
import ChartLine from 'ui/shared/chart/ChartLine';
import ChartOverlay from 'ui/shared/chart/ChartOverlay';
import ChartTooltip from 'ui/shared/chart/ChartTooltip';
import useTimeChartController from 'ui/shared/chart/useTimeChartController';

interface Props {
  data: TimeChartData;
  caption?: string;
}

const CHART_MARGIN = { bottom: 20, left: 40, right: 10, top: 10 };

const ChainIndicatorChartContent = ({ data }: Props) => {
  const overlayRef = React.useRef<SVGRectElement>(null);
  const lineColor = useColorModeValue('black', 'white');

  // Filter data to show only last 14 days
  const filteredData = React.useMemo(() => {
    if (!data[0]?.items) return data;

    const fourteenDaysAgo = new Date();
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);

    const filteredItems = data[0].items.filter(item =>
      new Date(item.date) >= fourteenDaysAgo,
    );

    return [ {
      ...data[0],
      items: filteredItems,
    } ];
  }, [ data ]);

  const axesConfig = React.useMemo(() => {
    return {
      x: { ticks: 3, nice: true },
      y: { ticks: 2, nice: true },
    };
  }, [ ]);

  const { rect, ref, axes, innerWidth, innerHeight, chartMargin } = useTimeChartController({
    data: filteredData,
    margin: CHART_MARGIN,
    axesConfig,
  });

  return (
    <svg width="100%" height="100%" ref={ ref } cursor="pointer">
      <g transform={ `translate(${ chartMargin.left || 0 },${ chartMargin.top || 0 })` } opacity={ rect ? 1 : 0 }>
        <ChartArea
          data={ filteredData[0].items }
          xScale={ axes.x.scale }
          yScale={ axes.y.scale }
          fill="url(#custom-chart-gradient)"
        />
        <ChartLine
          data={ filteredData[0].items }
          xScale={ axes.x.scale }
          yScale={ axes.y.scale }
          stroke={ lineColor }
          animation="left"
          strokeWidth={ 1.5 }
        />
        <ChartAxis
          type="left"
          scale={ axes.y.scale }
          ticks={ 2 }
          tickFormatGenerator={ axes.y.tickFormatter }
          noAnimation
        />
        <ChartAxis
          type="bottom"
          scale={ axes.x.scale }
          transform={ `translate(0, ${ innerHeight })` }
          ticks={ 3 }
          tickFormatGenerator={ axes.x.tickFormatter }
          noAnimation
        />
        <ChartOverlay ref={ overlayRef } width={ innerWidth } height={ innerHeight }>
          <ChartTooltip
            anchorEl={ overlayRef.current }
            width={ innerWidth }
            height={ innerHeight }
            xScale={ axes.x.scale }
            yScale={ axes.y.scale }
            data={ filteredData }
          />
        </ChartOverlay>
      </g>
    </svg>
  );
};

export default React.memo(ChainIndicatorChartContent);
